import React, { useEffect, useState } from 'react';
import { setupI18n, I18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';

const SUPPORTED_LOCALES = ['en', 'nb', 'sv', 'de', 'he'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const FALLBACK_LOCALE: SupportedLocale = 'en';

function resolveLocale(requested: string | null | undefined): SupportedLocale {
    if (requested && (SUPPORTED_LOCALES as readonly string[]).includes(requested)) {
        return requested as SupportedLocale;
    }
    if (typeof navigator !== 'undefined') {
        const browser = navigator.language?.split('-')[0];
        if (browser && (SUPPORTED_LOCALES as readonly string[]).includes(browser)) {
            return browser as SupportedLocale;
        }
    }
    return FALLBACK_LOCALE;
}

interface ShowroomI18nProviderProps {
    /** Locale from `?locale=…` query param. Falls back to browser language, then English. */
    requestedLocale?: string | null;
    children: React.ReactNode;
}

// Standalone Lingui instance for the public showroom — does not share state with the rest of the
// app. Lets the showroom run with its own locale (driven by the iframe `?locale=` param) without
// disturbing the parent app's translation state, and decouples it from Redux culture.
export const ShowroomI18nProvider: React.FC<ShowroomI18nProviderProps> = ({ requestedLocale, children }) => {
    const [i18n] = useState<I18n>(() => setupI18n());
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const locale = resolveLocale(requestedLocale);
        (async () => {
            try {
                const { messages } = await import(`../locales/${locale}/messages.mjs`);
                if (cancelled) return;
                i18n.load(locale, messages);
                i18n.activate(locale);
            } catch {
                // Fall back to English if the requested locale's bundle fails to load.
                if (cancelled) return;
                const { messages } = await import(`../locales/${FALLBACK_LOCALE}/messages.mjs`);
                if (cancelled) return;
                i18n.load(FALLBACK_LOCALE, messages);
                i18n.activate(FALLBACK_LOCALE);
            } finally {
                if (!cancelled) setReady(true);
            }
            if (typeof document !== 'undefined' && document.body) {
                document.body.dir = locale === 'he' ? 'rtl' : 'ltr';
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [i18n, requestedLocale]);

    if (!ready) return null;
    return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
};
